import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import { BillingUsageCalls } from './BillingUsageCalls';
import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import {
  useBillingUsageControllerReadQuery,
  type CompanyBillingSummaryDto,
} from '@shared/services/apiService';
import { billingDate, formatCreditUnits } from '../model/formatBillingUnits';
import { billingForecast } from '../model/billingForecast';
export const BillingUsage = ({
  companyId,
  summary,
  refreshSignal = 0,
}: {
  companyId: string;
  summary: CompanyBillingSummaryDto;
  refreshSignal?: number;
}) => {
  const theme = useTheme();
  const [campaignId, setCampaignId] = useState(''),
    [agentId, setAgentId] = useState(''),
    [showCalls, setShowCalls] = useState(false);
  const [period, setPeriod] = useState('current'),
    [from, setFrom] = useState(''),
    [to, setTo] = useState('');
  const today = new Date().toISOString().slice(0, 10);
  const args =
    period === 'current'
      ? {}
      : period === 'custom'
        ? {
            ...(from ? { from: `${from}T00:00:00Z` } : {}),
            ...(to && to < today ? { to: new Date(Date.parse(to) + 86400000).toISOString() } : {}),
          }
        : {
            from:
              new Date(Date.now() - (Number(period) - 1) * 86400000).toISOString().slice(0, 10) +
              'T00:00:00Z',
          };
  const invalid =
    period === 'custom' &&
    (!from || !to || from > to || to > today || Date.parse(to) - Date.parse(from) > 365 * 86400000);
  const unfiltered = useBillingUsageControllerReadQuery(
    { companyId, ...args },
    { skip: invalid, refetchOnMountOrArgChange: true },
  );
  const query = useBillingUsageControllerReadQuery(
    { companyId, ...args, ...(campaignId ? { campaignId } : {}), ...(agentId ? { agentId } : {}) },
    { skip: invalid, refetchOnMountOrArgChange: true },
  );
  const previous = useRef(refreshSignal);
  const { refetch } = query;
  useEffect(() => {
    if (previous.current !== refreshSignal) {
      previous.current = refreshSignal;
      if (!invalid) void refetch();
    }
  }, [refreshSignal, refetch, invalid]);
  const data = query.isError || invalid ? undefined : query.currentData;
  const credit = (units: string) => formatCreditUnits(units, data?.creditScale ?? 0);
  const forecast =
    data && period === 'current' && !campaignId && !agentId
      ? billingForecast(
          data,
          summary.endsAt,
          summary.totalAvailableUnits ?? summary.balance?.availableUnits ?? '0',
        )
      : null;
  const resources = unfiltered.currentData?.resources ?? [];
  const campaigns = [
    ...new Map(
      resources.filter((r) => r.campaignId).map((r) => [r.campaignId!, r.campaignName]),
    ).entries(),
  ];
  const agents = [
    ...new Map(
      resources
        .filter((r) => r.agentId && (!campaignId || r.campaignId === campaignId))
        .map((r) => [r.agentId!, r.agentName]),
    ).entries(),
  ];
  return (
    <Paper
      variant="outlined"
      component="section"
      aria-label="Credit usage"
      sx={{ p: { xs: 2, md: 3 } }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h6" component="h2">
              Credit usage
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Settled call costs, including top-ups. Active calls appear after settlement.
            </Typography>
          </Box>
          <TextField
            size="small"
            select
            label="Usage period"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            sx={{ minWidth: 180 }}>
            <MenuItem value="current">Current service period</MenuItem>
            <MenuItem value="7">Last 7 days</MenuItem>
            <MenuItem value="30">Last 30 days</MenuItem>
            <MenuItem value="custom">Custom dates</MenuItem>
          </TextField>
        </Stack>
        {period === 'custom' && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Usage from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="Usage to"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Stack>
        )}
        {invalid && (
          <Alert severity="info">
            Choose a valid date range of up to 366 days ending today or earlier.
          </Alert>
        )}
        {query.isFetching && (
          <Typography variant="body2" color="text.secondary">
            Loading usage…
          </Typography>
        )}
        {query.isError && (
          <Alert
            severity="warning"
            action={<Button onClick={() => void refetch()}>Retry usage</Button>}>
            Credit usage could not be loaded.
          </Alert>
        )}
        {data && (
          <>
            <Stack direction="row" spacing={{ xs: 2, md: 5 }} useFlexGap flexWrap="wrap">
              {[
                ['Credits used', credit(data.totalUnits)],
                ['From plan', credit(data.planUnits)],
                ['From top-ups', credit(data.topUpUnits)],
                ['Settled calls', data.calls.toLocaleString('en-GB')],
              ].map(([label, value]) => (
                <Box key={label}>
                  <Typography variant="body2" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography
                    variant="h5"
                    component="p"
                    sx={{ fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}>
                    {value}
                  </Typography>
                </Box>
              ))}
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                size="small"
                select
                label="Campaign"
                value={campaignId}
                onChange={(e) => {
                  setCampaignId(e.target.value);
                  setAgentId('');
                }}
                sx={{ minWidth: 180 }}>
                <MenuItem value="">All campaigns</MenuItem>
                {campaigns.map(([id, name]) => (
                  <MenuItem key={id} value={id}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                select
                label="Agent"
                value={agentId}
                onChange={(e) => setAgentId(e.target.value)}
                sx={{ minWidth: 180 }}>
                <MenuItem value="">All agents</MenuItem>
                {agents.map(([id, name]) => (
                  <MenuItem key={id} value={id}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
              <Button onClick={() => setShowCalls((v) => !v)}>
                {showCalls ? 'Hide billed calls' : 'Investigate billed calls'}
              </Button>
            </Stack>
            <Box
              role="img"
              aria-label={`Daily credit usage in UTC: ${credit(data.totalUnits)} credits`}
              sx={{ height: 220, minWidth: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data.days.map((d) => ({
                    ...d,
                    plan: Number(d.planUnits) / 10 ** data.creditScale,
                    topUp: Number(d.topUpUnits) / 10 ** data.creditScale,
                  }))}
                  margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke={theme.palette.divider} vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(d) => String(d).slice(5)}
                    tick={{ fill: theme.palette.text.secondary }}
                  />
                  <YAxis width={55} tick={{ fill: theme.palette.text.secondary }} />
                  <Tooltip
                    content={({ active, payload }) => {
                      const day = payload?.[0]?.payload as (typeof data.days)[number] | undefined;
                      return active && day ? (
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                          <Typography variant="caption">{billingDate(day.date)} · UTC</Typography>
                          <Typography variant="body2">Plan: {credit(day.planUnits)}</Typography>
                          <Typography variant="body2">Top-ups: {credit(day.topUpUnits)}</Typography>
                        </Paper>
                      ) : null;
                    }}
                  />
                  <Legend />
                  <Bar
                    dataKey="plan"
                    name="Plan credits"
                    stackId="credits"
                    fill={theme.palette.primary.main}
                  />
                  <Bar
                    dataKey="topUp"
                    name="Top-up credits"
                    stackId="credits"
                    fill={theme.palette.status.success.foreground}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
            {showCalls && (
              <BillingUsageCalls
                key={`${companyId}:${data.from}:${data.to}:${campaignId}:${agentId}`}
                args={{
                  companyId,
                  from: data.from,
                  to: data.to,
                  ...(campaignId ? { campaignId } : {}),
                  ...(agentId ? { agentId } : {}),
                }}
              />
            )}
            <Typography variant="caption" color="text.secondary">
              {billingDate(data.from)} – {billingDate(data.to)} · Posted dates in UTC · Current
              credit policy; accepted corrections are included.
            </Typography>
            {period === 'current' && !campaignId && !agentId && summary.endsAt && (
              <Typography
                variant="body2"
                color={
                  forecast?.shortfallUnits !== '0' && forecast ? 'warning.dark' : 'text.secondary'
                }>
                {forecast
                  ? `Estimated usage through ${billingDate(summary.endsAt)}: ${credit(forecast.projectedUnits)} credits${forecast.shortfallUnits !== '0' ? ` · ${credit(forecast.shortfallUnits)} more credits may be needed` : ''}. Based on ${forecast.sampleDays} complete days; future usage can change.`
                  : 'A forecast will appear after at least 3 complete days with recorded usage.'}
              </Typography>
            )}
            {!!data.resources.length ? (
              <Box role="table" aria-label="Usage by campaign and agent">
                <Box
                  role="row"
                  sx={{
                    display: { xs: 'none', sm: 'grid' },
                    gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr) 80px 100px',
                    gap: 2,
                    pb: 1,
                  }}>
                  {['Campaign', 'Agent', 'Calls', 'Credits used'].map((label) => (
                    <Typography
                      key={label}
                      role="columnheader"
                      variant="caption"
                      color="text.secondary">
                      {label}
                    </Typography>
                  ))}
                </Box>
                {data.resources.map((row) => (
                  <Box
                    role="row"
                    key={`${row.campaignId}:${row.agentId}`}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'minmax(0,1fr) auto',
                        sm: 'minmax(0,1fr) minmax(0,1fr) 80px 100px',
                      },
                      gap: 1,
                      py: 1.5,
                      borderTop: '1px solid',
                      borderColor: 'divider',
                      '& > *': { minWidth: 0, overflowWrap: 'anywhere' },
                    }}>
                    <Typography role="cell" variant="body2">
                      {row.campaignName}
                    </Typography>
                    <Typography role="cell" variant="body2" color="text.secondary">
                      {row.agentName}
                    </Typography>
                    <Typography role="cell" variant="body2">
                      {row.calls.toLocaleString('en-GB')} calls
                    </Typography>
                    <Typography role="cell" variant="body2" fontWeight={600}>
                      {credit(row.totalUnits)}
                      {(row.campaignId || row.agentId) && (
                        <Button
                          size="small"
                          onClick={() => {
                            setCampaignId(row.campaignId ?? '');
                            setAgentId(row.agentId ?? '');
                            setShowCalls(true);
                          }}>
                          View calls
                        </Button>
                      )}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No settled call usage in this date range.
              </Typography>
            )}
          </>
        )}
      </Stack>
    </Paper>
  );
};
